import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import DownloadIcon from '@mui/icons-material/Download';
import { AuditService } from '../../services/auditService';
import { downloadFile, readBlobErrorMessage, timestampedFilename } from '../../utils/downloadFile';
import {
  AuditLogEntry,
  AuditLogFilterOptions,
  AuditLogFilters,
  AuditLogQueryResponse,
} from '../../types/AuditLog';

const ANY = '';

/** Mirrors AuditQueryLimits.MaxPageSize on the server — the API clamps anything larger. */
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/**
 * Delay before a typed filter is committed. Every committed filter is a query, and every query on
 * this endpoint writes a row into the table being read — an undebounced field turns "Jane Doe"
 * into eight non-indexed scans and eight VIEW_AUDIT_LOG rows.
 */
const FILTER_DEBOUNCE_MS = 400;

/** Timestamps are stored and exported as UTC; the viewer shows local time with the zone named. */
function formatTimestamp(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });
}

function resultChipColor(result: string): 'success' | 'error' | 'warning' | 'default' {
  if (result.startsWith('Success')) return 'success';
  if (result.startsWith('Denied')) return 'error';
  if (result.startsWith('Failed')) return 'warning';
  if (result.startsWith('Reverted')) return 'warning';
  return 'default';
}

/**
 * Renders the stored `key=value;key=value` string as readable pairs.
 * Kept verbatim rather than prettified into prose — this is an access record, and a reader
 * comparing the page against a CSV export has to see the same text in both.
 */
function formatDetails(details: string | null): string {
  if (!details) return '—';
  return details.split(';').join(' · ');
}

export const AuditLogReport: React.FC = () => {
  const [response, setResponse] = useState<AuditLogQueryResponse | null>(null);
  const [filterOptions, setFilterOptions] = useState<AuditLogFilterOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [page, setPage] = useState(0); // MUI TablePagination is 0-based; the API is 1-based.
  const [pageSize, setPageSize] = useState(50);

  // Default to the last 30 days so the first load is bounded rather than the whole trail.
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [action, setAction] = useState(ANY);
  const [resourceType, setResourceType] = useState(ANY);
  const [result, setResult] = useState(ANY);

  // Typed filters are held twice: what the field shows, and what has actually been committed to a
  // query. Only the committed half participates in the fetch.
  const [userNameInput, setUserNameInput] = useState('');
  const [resourceIdInput, setResourceIdInput] = useState('');
  const [userName, setUserName] = useState('');
  const [resourceId, setResourceId] = useState('');

  const pendingTimers = useRef<{ userName?: number; resourceId?: number }>({});

  /**
   * Snapshot ceiling for the current filter, held in a ref rather than state: it must not
   * participate in the fetch dependencies or setting it after page 1 would refetch page 1.
   * Null means "start a new snapshot on the next query".
   */
  const snapshotMaxId = useRef<number | null>(null);

  const filters: AuditLogFilters = {
    startDate,
    endDate,
    action: action || undefined,
    resourceType: resourceType || undefined,
    result: result || undefined,
    userName: userName || undefined,
    resourceId: resourceId || undefined,
  };

  // Serialized so the fetch effect re-runs on a real filter change, not on every render.
  const filterKey = JSON.stringify(filters);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AuditService.queryAuditLog(
        JSON.parse(filterKey) as AuditLogFilters,
        page + 1,
        pageSize,
        snapshotMaxId.current
      );
      // First page of a filter establishes the snapshot; later pages reuse it.
      if (snapshotMaxId.current === null) snapshotMaxId.current = data.snapshotMaxId;
      setResponse(data);
    } catch {
      setError('Failed to load the audit trail.');
    } finally {
      setLoading(false);
    }
  }, [filterKey, page, pageSize]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  useEffect(() => {
    // Clear pending debounces if the panel closes mid-typing.
    const timers = pendingTimers.current;
    return () => {
      window.clearTimeout(timers.userName);
      window.clearTimeout(timers.resourceId);
    };
  }, []);

  useEffect(() => {
    AuditService.getFilterOptions()
      .then(setFilterOptions)
      .catch(() => {
        // Non-fatal: the dropdowns fall back to "Any" only. The trail itself still loads.
        setFilterOptions({ actions: [], resourceTypes: [], results: [] });
      });
  }, []);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await AuditService.exportAuditLog(filters);
      downloadFile(blob, timestampedFilename('audit-log'));
    } catch (err) {
      const message = await readBlobErrorMessage(err);
      setExportError(message ?? 'Failed to export the audit trail.');
    } finally {
      setExporting(false);
    }
  };

  // Any filter change invalidates both the current page position and the snapshot the previous
  // filter was frozen at.
  const resetToFirstPage = () => {
    snapshotMaxId.current = null;
    setPage(0);
  };

  const commitDebounced = (field: 'userName' | 'resourceId', commit: () => void) => {
    window.clearTimeout(pendingTimers.current[field]);
    pendingTimers.current[field] = window.setTimeout(() => {
      commit();
      resetToFirstPage();
    }, FILTER_DEBOUNCE_MS);
  };

  const entries: AuditLogEntry[] = response?.entries ?? [];
  const totalCount = response?.totalCount ?? 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Alert severity="info">
        <AlertTitle>Access record</AlertTitle>
        Every view, download, score change and deletion of call data is recorded here with the user,
        their role, the result and the source IP. Score changes also record what they changed — the
        previous and new values. Automated analysis runs are recorded as the AI Analysis Service.
        Opening and exporting this page is itself recorded.
      </Alert>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <TextField
            label="Start date"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); resetToFirstPage(); }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="End date"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); resetToFirstPage(); }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            select
            label="Action"
            size="small"
            value={action}
            onChange={(e) => { setAction(e.target.value); resetToFirstPage(); }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value={ANY}>Any action</MenuItem>
            {filterOptions?.actions.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <TextField
            select
            label="Resource type"
            size="small"
            value={resourceType}
            onChange={(e) => { setResourceType(e.target.value); resetToFirstPage(); }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value={ANY}>Any type</MenuItem>
            {filterOptions?.resourceTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField
            select
            label="Result"
            size="small"
            value={result}
            onChange={(e) => { setResult(e.target.value); resetToFirstPage(); }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value={ANY}>Any result</MenuItem>
            {filterOptions?.results.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
          <TextField
            label="User name"
            size="small"
            value={userNameInput}
            onChange={(e) => {
              const value = e.target.value;
              setUserNameInput(value);
              commitDebounced('userName', () => setUserName(value.trim()));
            }}
            helperText="Partial match, not case sensitive"
          />
          <TextField
            label="Call ID"
            size="small"
            value={resourceIdInput}
            onChange={(e) => {
              const value = e.target.value;
              setResourceIdInput(value);
              commitDebounced('resourceId', () => setResourceId(value.trim()));
            }}
            helperText="Who touched this recording"
          />
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting || totalCount === 0}
            aria-label="Export CSV — audit trail"
            sx={{ mb: 3 }}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </Box>
      </Paper>

      {exportError && (
        <Alert severity="warning" onClose={() => setExportError(null)}>
          <AlertTitle>Export not produced</AlertTitle>
          {exportError}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress aria-label="Loading audit trail" />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && totalCount === 0 && (
        <Alert severity="info">No audit records match these filters.</Alert>
      )}

      {!loading && !error && totalCount > 0 && (
        <Paper>
          <TableContainer>
            <Table size="small" aria-label="Audit trail">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>What changed</TableCell>
                  <TableCell>Source IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.auditLogId} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTimestamp(entry.timestamp)}</TableCell>
                    <TableCell>{entry.userName}</TableCell>
                    <TableCell>{entry.userRole}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{entry.action}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {entry.resourceId ? `${entry.resourceType}: ${entry.resourceId}` : entry.resourceType}
                    </TableCell>
                    <TableCell>
                      {/* Label carries the meaning; color is reinforcement only. */}
                      <Chip label={entry.result} size="small" color={resultChipColor(entry.result)} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', minWidth: 220 }}>
                      {formatDetails(entry.details)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {entry.ipAddress ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary">
        Times are shown in your local timezone. CSV exports are written in UTC.
      </Typography>
    </Box>
  );
};
